import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import { DatastoreDocument } from '@freelancer/datastore';
import { ProfileViewUsersCollection } from '@freelancer/datastore/collections';
import { Feature } from '@freelancer/feature-flags';
import { CardSize } from '@freelancer/ui/card';
import { trackByValue } from '@freelancer/ui/helpers';
import { ListItemPadding, ListItemType } from '@freelancer/ui/list-item';
import * as Rx from 'rxjs';
import { map } from 'rxjs/operators';
import { Md5 } from 'ts-md5/dist/md5';

@Component({
  selector: 'app-user-profile-verifications',
  template: `
    <fl-card
      *ngIf="
        profileViewUsersDocument.valueChanges() | flAsync as profileViewUsers;
        else loading
      "
      [size]="CardSize.SMALL"
    >
      <fl-card-header-title i18n="Profile verifications card title">
        Verifications
      </fl-card-header-title>

      <fl-list
        [outerPadding]="false"
        [padding]="ListItemPadding.XSMALL"
        [type]="ListItemType.NON_BORDERED"
      >
        <!-- Preferred freelancer -->
        <fl-list-item *flFeature="Feature.PREFERRED_FREELANCER_PROGRAM">
          <app-user-profile-verification-item
            labelText="Preferred Freelancer"
            i18n-labelText="
              Preferred freelancer profile verification list label
            "
            actionText="Join"
            i18n-actionText="
              Preferred freelancer profile verification list action
            "
            tooltipText="Member of our elite group of the best freelancers."
            i18n-tooltipText="Preferred freelancer tooltip message"
            [flTrackingLabel]="'VerificationPreferredFreelancer'"
            [status]="profileViewUsers.preferredFreelancer"
            [icon]="'ui-preferred'"
            [isOwner]="isOwner"
            [link]="'/preferred-freelancer-program'"
          >
          </app-user-profile-verification-item>
        </fl-list-item>

        <!-- KYC verified -->
        <fl-list-item>
          <app-user-profile-verification-item
            labelText="Identity Verified"
            i18n-labelText="Kyc verified profile verification list label"
            actionText="Verify"
            i18n-actionText="Kyc verified profile verification list action"
            tooltipText="Identity verified through a Government-issued ID check and a proof of address."
            i18n-tooltipText="Identity verified tooltip message"
            [flTrackingLabel]="'VerificationKycVerified'"
            [status]="profileViewUsers.status.identityVerified"
            [icon]="'ui-user'"
            [isOwner]="isOwner"
            [link]="'/users/kyc/verification-center-home'"
            [hideAction]="(hideKycVerificationItem$ | flAsync) === true"
          >
          </app-user-profile-verification-item>
        </fl-list-item>

        <!-- Payment verified -->
        <fl-list-item>
          <app-user-profile-verification-item
            labelText="Payment Verified"
            i18n-labelText="Payment verified profile verification list label"
            actionText="Verify"
            i18n-actionText="Payment verified profile verification list action"
            tooltipText="Verified to have a valid payment method."
            i18n-tooltipText="Payment verified tooltip message"
            [flTrackingLabel]="'VerificationPaymentVerifiedLink'"
            [status]="profileViewUsers.status.paymentVerified"
            [icon]="'ui-payment-verified'"
            [isOwner]="isOwner"
            [link]="'/payments/verify.php'"
          >
          </app-user-profile-verification-item>
        </fl-list-item>

        <!-- Phone verified -->
        <fl-list-item>
          <app-user-profile-verification-item
            labelText="Phone Verified"
            i18n-labelText="Phone verified profile verification list label"
            actionText="Verify"
            i18n-actionText="Phone verified profile verification list action"
            tooltipText="Verified to have a valid phone number."
            i18n-tooltipText="Phone number verified tooltip message"
            [flTrackingLabel]="'VerificationPhoneVerified'"
            [status]="profileViewUsers.status.phoneVerified"
            [icon]="'ui-phone'"
            [isOwner]="isOwner"
            [link]="'/users/settings.php'"
            [fragment]="'TrustSettings'"
          >
          </app-user-profile-verification-item>
        </fl-list-item>

        <!-- Email verified -->
        <fl-list-item>
          <app-user-profile-verification-item
            labelText="Email Verified"
            i18n-labelText="Email verified profile verification list label"
            actionText="Verify"
            i18n-actionText="Email verified profile verification list action"
            tooltipText="Verified to have a valid email address."
            i18n-tooltipText="Email verified tooltip message"
            [flTrackingLabel]="'VerificationEmailVerified'"
            [status]="profileViewUsers.status.emailVerified"
            [icon]="'ui-mail'"
            [isOwner]="isOwner"
            [link]="'/users/settings.php'"
            [fragment]="'EmailSettings'"
          >
          </app-user-profile-verification-item>
        </fl-list-item>

        <!-- Facebook connected -->
        <fl-list-item>
          <app-user-profile-verification-item
            labelText="Facebook Connected"
            i18n-labelText="Facebook profile verification list label"
            actionText="Connect"
            i18n-actionText="Facebook profile verification list action"
            tooltipText="Verified to have a Facebook account."
            i18n-tooltipText="Facebook account verified tooltip message"
            [flTrackingLabel]="'Verification'"
            [status]="profileViewUsers.status.facebookConnected"
            [icon]="'ui-facebook'"
            [isOwner]="isOwner"
            [link]="'/users/settings.php'"
            [fragment]="'/TrustSettings'"
          >
          </app-user-profile-verification-item>
        </fl-list-item>
      </fl-list>
    </fl-card>

    <ng-template #loading>
      <fl-card [size]="CardSize.SMALL">
        <fl-card-header-title i18n="Profile verifications card title">
          Verifications
        </fl-card-header-title>
        <fl-list
          [outerPadding]="false"
          [padding]="ListItemPadding.XSMALL"
          [type]="ListItemType.NON_BORDERED"
        >
          <fl-list-item
            *ngFor="let item of [0, 1, 2, 3, 4]; trackBy: trackByValue"
          >
            <app-user-profile-verification-item
              [loading]="true"
            ></app-user-profile-verification-item>
          </fl-list-item>
        </fl-list>
      </fl-card>
    </ng-template>
  `,
  styleUrls: ['./user-profile-verifications.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfileVerificationsComponent implements OnInit {
  CardSize = CardSize;
  Feature = Feature;
  ListItemPadding = ListItemPadding;
  ListItemType = ListItemType;
  trackByValue = trackByValue;

  @Input()
  profileViewUsersDocument: DatastoreDocument<ProfileViewUsersCollection>;
  @Input() isOwner: boolean;
  @Input() isLoggedIn: boolean;

  hideKycVerificationItem$: Rx.Observable<boolean>;

  private readonly _testCreator = 'moss';
  private readonly _testName = 'hide_voluntary_kyc';
  private readonly _testSplit = 0.3;

  ngOnInit(): void {
    this.hideKycVerificationItem$ = this.profileViewUsersDocument
      .valueChanges()
      .pipe(
        map(user => {
          // Don't show if the user is logged out.
          if (!this.isLoggedIn) {
            return true;
          }

          // If the user is not viewing their own profile, we should always show
          // the verification item.
          if (!this.isOwner) {
            return false;
          }

          // This copies the legacy implementation from 'ABTest.php:1583'
          // Hash the userId+testCreator+testName and get the first 8 chars.
          // This logic still exists as a rate limiting measure since the KYC
          // team can not process all the requests that this produces right now.
          // TODO: if possible change to use abtestService.getPercentageSplit
          const hashedString = Md5.hashStr(
            `${user.id}${this._testCreator}${this._testName}`,
          )
            .toString()
            .substring(0, 8);

          return parseInt(hashedString, 16) / 0xffffffff > this._testSplit;
        }),
      );
  }
}
